import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import {
  mapTransactionSourceTypeResourceToTransactionSourceType,
  mapTransactionSourceTypeToResource,
  mapTransactionTypeResourceToTransactionType,
  mapUserTransactionReasonResourceToUserTransactionReason,
} from '@/features/payouts/transaction-mappers'
import {
  ProHistoricalTransaction,
  type ProHistoricalTransactionProps,
} from './ProHistoricalTransaction'
import { collection } from '@/infra/collection'
import type { TransactionDateRange } from '@/features/payouts/transaction-date-range'
import { type ProHistoricalTransactionResource } from '@/features/payouts/api/resources/pro-transactions'
import { type PayoutsApi } from '@/features/payouts/api/pro-api'
import type {
  FetchUserDataFn,
  GetCurrentUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { FetchJobFn, PrimeJobFn } from '@/features/jobs'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import type { Id } from '@/infra/id'
import { maybeParseDate } from '@/utils/date-util'
import { memoize } from '@/utils/memoize-util'
import type { ProPayoutSummary } from './ProPayoutSummary'
import { mapProPayoutSummaryResourceToProPayoutSummary } from './pro-payout-summary-mappers'

/**
 * Manages pro-facing payouts.
 */
export class ProPayoutStore {
  /**
   * Historical transactions.
   */
  readonly transactions = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new ProHistoricalTransaction(),
  })

  constructor(
    private readonly _api: PayoutsApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeJob: PromiseLike<PrimeJobFn>,
    private readonly _fetchJob: FetchJobFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _fetchUserData: FetchUserDataFn,
  ) {}

  /**
   * Checks whether the current user is a Pro, once.
   */
  readonly checkCurrentUserIsPro = memoize(
    async () => {
      const userData = await this._fetchUserData(
        this._getCurrentUser().publicId,
      )
      return userData.userDataType === 'Pro'
    },
    { promise: true },
  )

  /**
   * Lists historical transactions.
   *
   * @param query
   * @param signal
   */
  async listHistoricalTransactions(
    query: ListHistoricalTransactionsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<ProHistoricalTransaction>> {
    const response = await this._api.transactions.listHistoricalTransactions(
      {
        page_size: query.limit,
        page_token: query.after,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_completed_from: query.dateCompleted?.from ?? undefined,
        date_completed_to: query.dateCompleted?.to ?? undefined,
        transaction_source:
          query.transactionSource
            ?.map(mapTransactionSourceTypeToResource)
            .join(',') ?? undefined,
        search_text: query.searchText,
        order_by: 'date_completed desc',
      },
      signal,
    )

    // For job-related transactions, fetch the jobs.
    // This is because `primeJob` does not do a set-and-fetch like the other
    // prime methods.
    const [primeJob] = await Promise.all([
      this._primeJob,
      ...response.data.map((dto) => this._fetchRelatedTransactionData(dto)),
    ])

    return {
      next: response.next_page_token,
      total: response.total_size,
      data: this.transactions.set(
        response.data.map((r) =>
          this._mapProHistoricalTransactionResourceToProps(r, primeJob),
        ),
      ),
    }
  }

  /**
   * Downloads the historical transactions as a CSV.
   *
   * @param query
   */
  async downloadHistoricalTransactionsAsCsv(
    query: DownloadHistoricalTransactionsAsCsvQuery = {},
  ): Promise<ReadableStream<Uint8Array>> {
    return await this._api.transactions.downloadHistoricalTransactionsAsCsv({
      workspace_ids: query.workspaceIds?.length
        ? query.workspaceIds?.join(',')
        : undefined,
      search_text: query.searchText,
      transaction_source:
        query.transactionSource
          ?.map(mapTransactionSourceTypeToResource)
          .join(',') ?? undefined,
      date_completed_from: query.dateCompleted?.from ?? undefined,
      date_completed_to: query.dateCompleted?.to ?? undefined,
    })
  }

  /**
   * Fetches the current user's payout summary.
   */
  async fetchPayoutSummary(signal?: AbortSignal): Promise<ProPayoutSummary> {
    const response = await this._api.analytics.getProPayoutSummary(signal)
    return mapProPayoutSummaryResourceToProPayoutSummary(response)
  }

  /**
   * Fetches related transaction data for the given transaction.
   * @param dto
   * @private
   */
  private async _fetchRelatedTransactionData(
    dto: ProHistoricalTransactionResource,
  ) {
    if (dto.job_id) {
      try {
        await this._fetchJob(dto.job_id)
      } catch (err) {
        // Some jobs may no longer exist or have been archived.
        // This is expected, so we'll ignore it here.
        console.warn(
          `Unable to retrieve associated job ${dto.job_id} for transaction`,
          err,
        )
      }
    }
  }

  private _mapProHistoricalTransactionResourceToProps(
    resource: ProHistoricalTransactionResource,
    primeJob: PrimeJobFn,
  ): Partial<ProHistoricalTransactionProps> {
    return {
      id: resource.id,
      shortId: resource.short_id,
      amount: resource.amount,
      completedAt: maybeParseDate(resource.date_completed),
      payoutId: resource.payout_id,
      stripeAccountId: resource.stripe_account_id,
      workspace: resource.workspace_id
        ? this._primeWorkspace({ id: resource.workspace_id })
        : undefined,
      type: mapTransactionTypeResourceToTransactionType(resource.type),
      source: mapTransactionSourceTypeResourceToTransactionSourceType(
        resource.source,
      ),
      reason:
        resource.reason &&
        mapUserTransactionReasonResourceToUserTransactionReason(
          resource.reason,
        ),
      memo: resource.memo,
      job:
        resource.job_id === undefined
          ? undefined
          : resource.job_id === null
            ? null
            : primeJob({
                id: resource.job_id,
                shortId: resource.job_short_id ?? undefined,
              }),
    }
  }
}

/**
 * Lists historical transactions.
 */
export interface ListHistoricalTransactionsQuery extends CursorPageQuery {
  workspaceIds?: Id[] | null
  dateCompleted?: TransactionDateRange | null
  transactionSource?: TransactionSourceType[] | null
  searchText?: string
}

/**
 * Downloads historical transactions as a CSV.
 */
export interface DownloadHistoricalTransactionsAsCsvQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: TransactionDateRange | null
  transactionSource?: TransactionSourceType[] | null
  searchText?: string
}
